'use client'

import { useMemo, useState } from 'react'
import { useLocale } from 'next-intl'

import { HOLIDAY_PACKAGE_SORT, type HolidayPackageSort } from '@/src/constants/holidays'
import type { HolidayPackagesIndexEntry } from '@/src/types/holidays'
import { sortHolidayPackages } from '@/src/utils/holidays/sortHolidayPackages'
import {
    buildCityFacets,
    buildMonthFacets,
    buildOccasionFacets,
    buildThemeFacets,
    filterHolidayPackages,
    hasActiveHolidayPackageFilters
} from '@/src/utils/holidays/packageFilters'

import usePackageFilters from './usePackageFilters'

/**
 * Everything the all-packages listing renders from, shared by the desktop and mobile containers so
 * the two cannot drift. Facets are built from the whole catalogue, not the filtered result, so the
 * counts beside each checkbox hold still while the reader ticks boxes.
 */
export const useHolidayPackagesList = (entries: HolidayPackagesIndexEntry[]) => {
    const locale = useLocale()
    const {
        filters,
        toggleCity,
        toggleMonth,
        toggleOccasion,
        toggleTheme,
        applySearch,
        clearAll
    } = usePackageFilters()
    const [sort, setSort] = useState<HolidayPackageSort>(HOLIDAY_PACKAGE_SORT.PRICE_LOW_TO_HIGH)

    const cityFacets = useMemo(() => buildCityFacets(entries), [entries])
    const monthFacets = useMemo(() => buildMonthFacets(entries), [entries])
    const occasionFacets = useMemo(() => buildOccasionFacets(entries), [entries])
    const themeFacets = useMemo(() => buildThemeFacets(entries), [entries])

    const visiblePackages = useMemo(
        () => sortHolidayPackages(filterHolidayPackages(entries, filters), sort, locale),
        [entries, filters, sort, locale]
    )

    return {
        filters,
        cityFacets,
        monthFacets,
        occasionFacets,
        themeFacets,
        sort,
        setSort,
        visiblePackages,
        totalPackages: entries.length,
        hasActiveFilters: hasActiveHolidayPackageFilters(filters),
        activeFilterCount:
            filters.cities.length + filters.months.length + filters.occasions.length + filters.themes.length,
        toggleCity,
        toggleMonth,
        toggleOccasion,
        toggleTheme,
        applySearch,
        clearAll
    }
}

export default useHolidayPackagesList
